/**
 * 放送地点に紐づく `contents[]` の編集 UI。
 *
 * - カテゴリのドロップダウン (自列車遅延 / 接続線遅延 / 任意テキスト) を切り替えると、
 *   そのカテゴリに必要な入力フィールドが動的に出現する。
 * - 各コンテンツは「↑」「↓」ボタンで並び替え、「× 削除」ボタンで除去できる。
 * - 「＋ コンテンツを追加」ボタンで末尾にデフォルト値のコンテンツを追加する。
 *
 * 状態管理は親コンポーネント (BroadcastPointForm) が保持し、本コンポーネントは
 * 受け取ったコンテンツ配列を表示・編集して onChange で返すだけのコントロールド
 * コンポーネントとして実装する。
 */
import type {
  BroadcastContent,
  BroadcastContentCategory,
  BroadcastSchedule,
} from '../../api/types';

import { useEffect, useState } from 'react';

import {
  CATEGORY_META,
  cloneContent,
  createDefaultContent,
  getCategoryColor,
  getCategoryMeta,
  moveContent,
} from '../../utils/broadcastContents';
import { BroadcastScheduleEditor } from '../BroadcastScheduleEditor';
import { ContentPropertyFields } from './ContentPropertyFields';
import { PlaybackRateField } from './parts/PlaybackRateField';
import type { BroadcastContentsEditorProps } from './types';

export function BroadcastContentsEditor({
  contents,
  onChange,
  routes = [],
  routeStops = {},
  parentRouteId,
  parentVoiceId,
  onUploadAudio,
  onPreviewSsml,
}: BroadcastContentsEditorProps): React.JSX.Element {
  // アコーディオンで詳細表示するコンテンツの index。
  // 複数コンテンツがある場合、この 1 件のみ詳細 (スケジュール + プロパティ) を表示し、
  // 他は折りたたむ (ヘッダーのみ)。コンテンツが 1 件のときは常にその 1 件を開く。
  const [openIndex, setOpenIndex] = useState<number>(0);

  // コンテンツ件数が変わった (削除等) 際に openIndex が範囲外にならないよう補正する。
  useEffect(() => {
    if (contents.length === 0) return;
    if (openIndex > contents.length - 1) {
      setOpenIndex(contents.length - 1);
    }
  }, [contents.length, openIndex]);

  const updateAt = (index: number, content: BroadcastContent): void => {
    const next = contents.slice();
    next[index] = content;
    onChange(next);
  };

  const removeAt = (index: number): void => {
    const next = contents.slice();
    next.splice(index, 1);
    onChange(next);
  };

  const moveUp = (index: number): void => {
    onChange(moveContent(contents, index, index - 1));
  };
  const moveDown = (index: number): void => {
    onChange(moveContent(contents, index, index + 1));
  };

  const handleAdd = (): void => {
    // 追加したコンテンツ (末尾) を自動的に開く。
    setOpenIndex(contents.length);
    onChange([...contents, createDefaultContent('self_train_delay')]);
  };

  const handleCategoryChange = (index: number, category: BroadcastContentCategory): void => {
    // カテゴリを変えたら properties は新カテゴリのデフォルト値で初期化する
    updateAt(index, createDefaultContent(category));
  };

  /** 複数コンテンツのときだけアコーディオン (折りたたみ) を有効にする。 */
  const accordionEnabled = contents.length > 1;
  const isOpen = (index: number): boolean => !accordionEnabled || openIndex === index;

  return (
    <div className="contents-editor">
      <div className="contents-editor-header">
        <strong>コンテンツ ({contents.length} 件)</strong>
        <button
          type="button"
          data-testid="add-content-button"
          aria-label="コンテンツを追加"
          onClick={handleAdd}
        >
          ＋ コンテンツを追加
        </button>
      </div>

      {contents.length === 0 ? (
        <div className="empty-state" style={{ padding: '12px 8px' }}>
          コンテンツは設定されていません。「＋ コンテンツを追加」から作成してください。
        </div>
      ) : null}

      <ol className="contents-list">
        {contents.map((content, index) => {
          const open = isOpen(index);
          return (
            <li
              key={index}
              className={`contents-item${accordionEnabled && !open ? ' contents-item--collapsed' : ''}`}
              data-testid={`content-row-${index}`}
              style={{ background: getCategoryColor(content.category) }}
            >
              <div className="contents-item-header">
                {accordionEnabled ? (
                  <button
                    type="button"
                    className="contents-item-toggle"
                    data-testid={`content-toggle-${index}`}
                    aria-expanded={open}
                    aria-label={`コンテンツ#${index + 1} を${open ? '折りたたむ' : '展開する'}`}
                    onClick={() => setOpenIndex(open ? -1 : index)}
                  >
                    {open ? '▼' : '▶'}
                  </button>
                ) : null}
                <span className="contents-item-index">#{index + 1}</span>
                {/* 折りたたみ時にも種別ラベルが見えるようにする */}
                <span className="contents-item-category-label">
                  {getCategoryMeta(content.category)?.label}
                </span>

                {/*
                 * カテゴリ <select>。E2E / AI エージェントが画面トップの一覧フィルタ
                 * (id="content-filter") と取り違えやすいので、明示的な data-testid を
                 * 付与してモーダル内のコンテンツカテゴリ <select> を一意に取得できるようにする。
                 * testid は 0 始まりの index を用いる (画面表示の "#i" は 1 始まりだが、
                 * 他の content-row-{index} と同じ流儀にする)。
                 */}
                <select
                  aria-label={`コンテンツ#${index + 1} カテゴリ`}
                  data-testid={`content-category-select-${index}`}
                  value={content.category}
                  onChange={(e) =>
                    handleCategoryChange(index, e.target.value as BroadcastContentCategory)
                  }
                >
                  {/*
                   * カテゴリ選択肢:
                   * - 新規作成用のドロップダウンからは旧「接続線遅延」(`connecting_line_delay`) を
                   *   非表示にする (仕様変更: 乗換駅ベースの新カテゴリ
                   *   `connecting_line_delay_by_station` に一本化)。ただしバックエンド側の
                   *   処理・データモデル・既存レコードの表示編集は残っているため、
                   *   `CATEGORY_META` からは削除せず UI レベルでのみ絞り込む。
                   * - 既存レコード編集時に現在値が絞り込みで消えると <select> が空表示に
                   *   なってしまうので、現在の `content.category` が絞り込み対象と一致する
                   *   場合はその option を明示的に残す (下記 filter 条件の第二項)。
                   */}
                  {CATEGORY_META.filter(
                    (m) =>
                      m.category !== 'connecting_line_delay' || m.category === content.category,
                  ).map((m) => (
                    <option key={m.category} value={m.category}>
                      {m.label}
                    </option>
                  ))}
                </select>

                {/*
                 * 常時放送バッジはカード見出しからは削除した (Editor ボタン側で
                 * `schedule-summary-${index}` として同等以上のサマリを表示するため冗長)。
                 * 詳細は BroadcastScheduleEditor.tsx のコメント参照。
                 */}
                <div className="contents-item-actions">
                  <button
                    type="button"
                    onClick={() => moveUp(index)}
                    disabled={index === 0}
                    aria-label={`コンテンツ#${index + 1} を上へ`}
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveDown(index)}
                    disabled={index === contents.length - 1}
                    aria-label={`コンテンツ#${index + 1} を下へ`}
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={() => removeAt(index)}
                    aria-label={`コンテンツ#${index + 1} を削除`}
                  >
                    × 削除
                  </button>
                </div>
              </div>
              {/*
               * アコーディオン折りたたみ時 (accordionEnabled かつ !open) は、
               * カテゴリ説明・スケジュール・プロパティ入力を非表示にしてヘッダーのみ表示する。
               * ただしフォーム値 (values.contents) 自体は保持されるため、保存時の
               * バリデーションは折りたたまれたコンテンツも対象になる。
               */}

              {open ? (
                <>
                  <div className="contents-item-description">
                    {getCategoryMeta(content.category)?.description}
                  </div>
                  {/*
                   * スケジュール設定 (曜日 / 時間帯 / 有効期間)。
                   *
                   * カテゴリ説明の直下 (プロパティフィールドの上) に配置し、ユーザが編集を
                   * 始める前にスケジュールを設定できることを可視化する。折りたたみ状態でも
                   * 現在の設定サマリ (曜日 / 時間帯 / 有効期間, もしくは「常時放送」) が
                   * ボタン内のバッジに表示される。
                   *
                   * 詳細は `BroadcastScheduleEditor` を参照。値変更時は schedule プロパティ
                   * のみ差し替えて `updateAt` で反映する。
                   */}
                  <BroadcastScheduleEditor
                    value={content.schedule}
                    contentIndex={index}
                    onChange={(nextSchedule: BroadcastSchedule | undefined) => {
                      const nextContent: BroadcastContent = { ...cloneContent(content) };
                      if (nextSchedule === undefined) {
                        delete nextContent.schedule;
                      } else {
                        nextContent.schedule = nextSchedule;
                      }
                      updateAt(index, nextContent);
                    }}
                  />
                  {/*
                   * 再生速度 (playback_rate) 設定 (任意)。
                   *
                   * 端末側の HTMLAudioElement.playbackRate / ExoPlayer.setPlaybackSpeed に
                   * 反映される。1.0 (等倍) はキーごと保存しない (端末側デフォルト)。
                   * プリセット 0.75/0.85/1.0/1.15/1.25 + 自由入力 (0.5〜2.0) の 2 方式を提供。
                   *
                   * 値変更時は playback_rate プロパティのみ差し替えて updateAt で反映する。
                   */}
                  <PlaybackRateField
                    contentIndex={index}
                    value={content.playback_rate}
                    onChange={(nextRate: number | undefined) => {
                      const nextContent: BroadcastContent = { ...cloneContent(content) };
                      if (nextRate === undefined || nextRate === 1) {
                        delete nextContent.playback_rate;
                      } else {
                        nextContent.playback_rate = nextRate;
                      }
                      updateAt(index, nextContent);
                    }}
                  />
                  <ContentPropertyFields
                    content={content}
                    routes={routes}
                    routeStops={routeStops}
                    parentRouteId={parentRouteId}
                    parentVoiceId={parentVoiceId}
                    onUploadAudio={onUploadAudio}
                    onPreviewSsml={onPreviewSsml}
                    onChange={(c) => updateAt(index, c)}
                  />
                </>
              ) : null}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
